import { supportReturnTarget } from './return-target';
import { InviteSetup } from './invites';
import { InstHeader } from './inst-header';
import { AuditNavigation } from './audit-links';
import { enumText } from './presentation';
import brandWordmark from './assets/panpa-wordmark.svg';
import { canLeaveDrafts } from './drafts';
import { EnvironmentBanner } from './environment-banner';
import { CapabilitiesContext, sectionVisible } from './access';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { api, ApiError, getToken, setToken } from './api';
import { PageState, DialogPanel } from './ui';
import { CommandPalette, NavigationDrawer } from './navigation';

const PermissionsPage = lazy(() =>
  import('./permissions').then((m) => ({ default: m.PermissionsPage })),
);
const StaffPage = lazy(() => import('./staff').then((m) => ({ default: m.StaffPage })));
const BansPage = lazy(() => import('./bans').then((m) => ({ default: m.BansPage })));
const RolesPage = lazy(() => import('./roles').then((m) => ({ default: m.RolesPage })));
const MaintenancePage = lazy(() =>
  import('./maintenance').then((m) => ({ default: m.MaintenancePage })),
);
const FeedbackPage = lazy(() => import('./feedback').then((m) => ({ default: m.FeedbackPage })));

const AnnouncementsPage = lazy(() =>
  import('./pages').then((m) => ({ default: m.AnnouncementsPage })),
);
const RadioPage = lazy(() => import('./radio').then((m) => ({ default: m.RadioPage })));
const BotsPage = lazy(() => import('./bots').then((m) => ({ default: m.BotsPage })));
const SlotPage = lazy(() => import('./slot').then((m) => ({ default: m.SlotPage })));
const ChatPage = lazy(() => import('./pages').then((m) => ({ default: m.ChatPage })));
const DailyBonusPage = lazy(() => import('./pages').then((m) => ({ default: m.DailyBonusPage })));
const FlagsPage = lazy(() => import('./pages').then((m) => ({ default: m.FlagsPage })));
const SalonsPage = lazy(() => import('./pages').then((m) => ({ default: m.SalonsPage })));
const StatsPage = lazy(() => import('./pages').then((m) => ({ default: m.StatsPage })));
const StorePage = lazy(() => import('./pages').then((m) => ({ default: m.StorePage })));
const UsersPage = lazy(() => import('./pages').then((m) => ({ default: m.UsersPage })));
const WheelPage = lazy(() => import('./wheel').then((m) => ({ default: m.WheelPage })));
const AuditPage = lazy(() => import('./audit').then((m) => ({ default: m.AuditPage })));
const LivePage = lazy(() => import('./live').then((m) => ({ default: m.LivePage })));
const EventsPage = lazy(() => import('./events').then((m) => ({ default: m.EventsPage })));
const EconomyPage = lazy(() => import('./economy').then((m) => ({ default: m.EconomyPage })));
const EkonomiVarsayilanPage = lazy(() =>
  import('./ekonomi-varsayilan').then((m) => ({ default: m.EkonomiVarsayilanPage })),
);
const ReportsPage = lazy(() => import('./reports').then((m) => ({ default: m.ReportsPage })));
const ModerasyonKuyrugu = lazy(() =>
  import('./features/moderasyon/kuyruk').then((m) => ({ default: m.ModerasyonKuyrugu })),
);
const WebOdemelerPage = lazy(() =>
  import('./features/web-odemeler/web-odemeler').then((m) => ({ default: m.WebOdemelerPage })),
);
const PoliciesPage = lazy(() => import('./policies').then((m) => ({ default: m.PoliciesPage })));

const PhotosPage = lazy(() => import('./photos').then((m) => ({ default: m.PhotosPage })));
const SystemPage = lazy(() => import('./system').then((m) => ({ default: m.SystemPage })));
const GamesPage = lazy(() => import('./games').then((m) => ({ default: m.GamesPage })));
const PerfPage = lazy(() => import('./perf').then((m) => ({ default: m.PerfPage })));
const RaporlarPage = lazy(() => import('./raporlar').then((m) => ({ default: m.RaporlarPage })));

const SECTIONS = [
  ['system', 'Sistem Sağlığı'],
  ['perf', 'Cihaz Performansı'],
  ['games', 'Oyunlar'],
  ['live', 'Canlı'],
  ['events', 'Olay Şeridi'],
  ['economy', 'Muhasebe'],
  ['ekonomi-varsayilan', 'Ekonomi Varsayılanı'],
  ['web-odemeler', 'Web Ödemeleri'],
  ['moderasyon', 'Kuyruk'],
  ['reports', 'Şikayetler'],
  ['photos', 'Fotoğraf Onayları'],
  ['bans', 'Yasaklar'],
  ['feedback', 'Geri Bildirimler'],
  ['roles', 'Oyun İçi Roller'],
  ['permissions', 'Yetkilerim'],
  ['staff', 'Panel Çalışanları'],
  ['maintenance', 'Bakım'],
  ['stats', 'Bugün'],
  ['users', 'Oyuncu Merkezi'],
  ['salons', 'Salonlar & Kurallar'],
  ['store', 'Mağaza Katalogu'],
  ['policies', 'Politikalar'],
  ['daily', 'Günlük Bonus'],
  ['wheel', 'Çarkıfelek'],
  ['slot', 'Slot Oyunları'],
  ['radio', 'Radyo'],
  ['bots', 'Botlar'],
  ['chat', 'Sohbet Odaları'],
  ['announcements', 'Duyurular'],
  ['flags', 'Bayraklar & Kill-switch'],
  ['audit', 'Denetim Günlüğü'],
  ['raporlar', 'Raporlar (Excel)'],
] as const;
type Section = (typeof SECTIONS)[number][0];

// Bilgi mimarisi (docs/56 §3): bugün → oyuncular → moderasyon → canlı →
// ekonomi → oyun ayarları → kayıtlar → ekip → sistem.
const GROUPS: { title: string; keys: Section[] }[] = [
  { title: 'Bugün', keys: ['stats'] },
  { title: 'Oyuncular', keys: ['users'] },
  { title: 'Moderasyon', keys: ['moderasyon', 'reports', 'photos', 'feedback', 'bans', 'chat'] },
  { title: 'Canlı', keys: ['live', 'maintenance', 'radio'] },
  {
    title: 'Ekonomi',
    keys: ['economy', 'ekonomi-varsayilan', 'web-odemeler', 'store', 'daily', 'wheel'],
  },
  {
    title: 'Oyun Ayarları',
    // Slot oyunları oyun ayarlarında (kullanıcı 25.10); yetki yine slot.manage
    keys: ['salons', 'games', 'slot', 'policies', 'bots', 'announcements', 'flags'],
  },
  { title: 'Kayıtlar', keys: ['raporlar', 'audit', 'events'] },
  { title: 'Ekip', keys: ['staff', 'roles', 'permissions'] },
  { title: 'Sistem', keys: ['system', 'perf'] },
];
type AdminSession = { email: string; role: string; capabilities: string[] };

export function App() {
  const [admin, setAdmin] = useState<AdminSession | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState<number | null>(null);
  useEffect(() => {
    if (!admin || admin.role === 'FINANCE') {
      setFeedbackOpen(null);
      return;
    }
    let cancelled = false;
    const refresh = () => {
      void api
        .feedback('open', '', 25) // sunucu yalnız 25/50/100 kabul eder; sayaç `open` alanından
        .then((r) => {
          if (!cancelled) setFeedbackOpen(r.open);
        })
        .catch(() => {
          if (!cancelled) setFeedbackOpen(null);
        });
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener('feedback-changed', refresh);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('feedback-changed', refresh);
    };
  }, [admin]);
  const [checked, setChecked] = useState(false);
  const [reauth, setReauth] = useState(false);
  const [sessionSoon, setSessionSoon] = useState(false);
  const [sessionGeneration, setSessionGeneration] = useState(0);
  useEffect(() => {
    if (!admin) return;
    const check = () => {
      const token = getToken();
      if (!token) {
        setReauth(true);
        return;
      }
      try {
        const claim = JSON.parse(
          atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')),
        ) as { exp?: number };
        if (typeof claim.exp === 'number') {
          const left = claim.exp * 1000 - Date.now();
          setSessionSoon(left > 0 && left < 600000);
          if (left <= 0) setReauth(true);
        }
      } catch {
        /* Opaque oturumda süre sunucunun 401 yanıtından anlaşılır. */
      }
    };
    check();
    const timer = setInterval(check, 5000);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, [admin]);
  const [active, setActive] = useState<Section>(() => {
    const key = location.hash.slice(2).split('?')[0]?.split('/')[0];
    return SECTIONS.some(([s]) => s === key) ? (key as Section) : 'stats';
  });
  useEffect(() => {
    if (admin?.role === 'FINANCE' && (!location.hash || location.hash === '#/')) {
      setActive('economy');
      location.hash = '/economy';
    }
  }, [admin?.role]);
  const [routePath, setRoutePath] = useState(location.hash);
  const acceptedSection = useRef(active);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = (key: Section) => {
    if (key !== active && !canLeaveDrafts()) return;
    acceptedSection.current = key;
    location.hash = `/${key}`;
    setActive(key);
    setMenuOpen(false);
  };
  useEffect(() => {
    const change = () => {
      const key = location.hash.slice(2).split('?')[0]?.split('/')[0];
      if (!SECTIONS.some(([s]) => s === key)) return;
      if (key !== acceptedSection.current && !canLeaveDrafts()) {
        history.replaceState(null, '', `#/${acceptedSection.current}`);
        return;
      }
      acceptedSection.current = key as Section;
      setActive(key as Section);
      setRoutePath(location.hash);
    };
    const shortcut = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('hashchange', change);
    window.addEventListener('keydown', shortcut);
    return () => {
      window.removeEventListener('hashchange', change);
      window.removeEventListener('keydown', shortcut);
    };
  }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navQuery, setNavQuery] = useState('');
  const openUser = (id: string) => {
    if (!canLeaveDrafts()) return;
    acceptedSection.current = 'users';
    const source = supportReturnTarget(location.hash);
    location.hash = `/users/${encodeURIComponent(id)}${source ? `?return=${encodeURIComponent(source)}` : ''}`;
    setActive('users');
  };
  const openAudit = (id: string) => {
    if (!canLeaveDrafts()) return;
    acceptedSection.current = 'audit';
    location.hash = `/audit/${encodeURIComponent(id)}`;
    setActive('audit');
  };

  useEffect(() => {
    if (!getToken()) {
      setChecked(true);
      return;
    }
    api
      .me()
      .then((r) =>
        setAdmin({ email: r.admin.email, role: r.admin.role, capabilities: r.capabilities ?? [] }),
      )
      .catch(() => setToken(null))
      .finally(() => setChecked(true));
  }, []);

  const inviteToken = location.hash.match(/^#\/invite\/([A-Za-z0-9_-]{43})$/)?.[1];
  if (inviteToken) return <InviteSetup token={inviteToken} />;
  if (!checked)
    return (
      <div className="center">
        <PageState loading />
      </div>
    );
  if (!admin) return <Login onLogin={(a) => setAdmin(a)} />;

  // Web TL bakiyesi yalnız muhasebe (finance.read: SUPER_ADMIN, FINANCE); sunucu da reddeder
  const visibleSections = SECTIONS.filter(([key]) =>
    key === 'web-odemeler'
      ? admin.capabilities.includes('finance.read')
      : sectionVisible(key, admin.role, admin.capabilities),
  );
  const visibleGroups = GROUPS.map((g) => ({
    ...g,
    title: g.title,
    keys: g.keys.filter((k) => visibleSections.some(([key]) => key === k)),
  })).filter((g) => g.keys.length);
  const sectionAllowed = visibleSections.some(([key]) => key === active);
  const navigation = (
    <>
      {' '}
      <div className="brand">
        <img className="brand-logo" src={brandWordmark} alt="Panpa" />
        <span>
          <small>
            Yönetim
            <br />
            merkezi
          </small>
        </span>
      </div>
      <label className="nav-search">
        Bölüm bul
        <input
          type="search"
          value={navQuery}
          onChange={(e) => setNavQuery(e.target.value)}
          placeholder="Salon, oyuncu, politika…"
        />
      </label>
      <nav aria-label="Yönetim bölümleri">
        {visibleGroups.map((group) => {
          const sections = visibleSections.filter(
            ([key, label]) =>
              group.keys.includes(key) &&
              label.toLocaleLowerCase('tr').includes(navQuery.toLocaleLowerCase('tr')),
          );
          return (
            sections.length > 0 && (
              <details
                className="nav-group"
                key={`${group.title}-${active}-${Boolean(navQuery)}`}
                open={Boolean(navQuery) || group.keys.includes(active)}
              >
                <summary>{group.title}</summary>
                {sections.map(([key, label]) => (
                  <button
                    key={key}
                    aria-current={key === active ? 'page' : undefined}
                    className={key === active ? 'active' : ''}
                    onClick={() => {
                      navigate(key);
                      setMenuOpen(false);
                      requestAnimationFrame(() => {
                        const heading = document.querySelector<HTMLElement>('#main-content h1');
                        heading?.setAttribute('tabindex', '-1');
                        heading?.focus();
                      });
                    }}
                  >
                    {label}
                    {key === 'feedback' && feedbackOpen != null && (
                      <span className="pill">{feedbackOpen}</span>
                    )}
                  </button>
                ))}
              </details>
            )
          );
        })}
      </nav>
      {navQuery &&
        !SECTIONS.some(([, label]) =>
          label.toLocaleLowerCase('tr').includes(navQuery.toLocaleLowerCase('tr')),
        ) && (
          <p role="status" className="muted">
            Bölüm bulunamadı.
          </p>
        )}
      <div className="spacer" />
      <div className="who">
        <div>{admin.email}</div>
        <div className="muted">{enumText(admin.role)}</div>
        <button
          className="link"
          onClick={async () => {
            if (!canLeaveDrafts()) return;
            await api.logout().catch(() => {});
            setToken(null);
            setAdmin(null);
          }}
        >
          Çıkış
        </button>
      </div>
    </>
  );
  return (
    <CapabilitiesContext.Provider value={admin.capabilities}>
      <AuditNavigation.Provider value={openAudit}>
        {reauth && (
          <DialogPanel label="Oturumu yenile" onClose={() => {}}>
            <div className="session-dialog">
              <p className="session-notice">
                Oturumunu yenile. Aynı hesapla giriş yapınca açık form taslağın burada kalır; işlem
                otomatik tekrarlanmaz.
              </p>
              <Login
                onLogin={(next) => {
                  if (next.email !== admin.email) setSessionGeneration((v) => v + 1);
                  setAdmin(next);
                  setReauth(false);
                  setSessionSoon(false);
                }}
              />
            </div>
          </DialogPanel>
        )}
        <div className="shell inst-shell">
          <a className="skip-link" href="#main-content">
            İçeriğe geç
          </a>
          <InstHeader
            sections={visibleSections}
            groups={visibleGroups}
            active={active}
            email={admin.email}
            role={admin.role}
            feedbackOpen={feedbackOpen}
            menuOpen={menuOpen}
            onMenu={() => setMenuOpen(true)}
            onSearch={() => setPaletteOpen(true)}
            onNavigate={(key) => navigate(key as Section)}
            onLogout={async () => {
              if (!canLeaveDrafts()) return;
              await api.logout().catch(() => {});
              setToken(null);
              setAdmin(null);
            }}
          />
          <NavigationDrawer open={menuOpen} onClose={() => setMenuOpen(false)}>
            <aside className="nav" id="main-nav">
              {menuOpen && navigation}
            </aside>
          </NavigationDrawer>
          {paletteOpen && (
            <CommandPalette
              allowUsers={admin.role !== 'FINANCE'}
              sections={visibleSections}
              onOpenUser={openUser}
              onOpenAudit={openAudit}
              onSelect={(key) => navigate(key as Section)}
              onClose={() => setPaletteOpen(false)}
            />
          )}
          <main className="content" id="main-content" tabIndex={-1}>
            {sessionSoon && (
              <p className="session-notice">
                Oturumun 10 dakika içinde sona erecek.{' '}
                <button onClick={() => setReauth(true)}>Girişi yenile</button>
              </p>
            )}
            <div className="inst-page-context">
              <EnvironmentBanner />
              <p className="inst-breadcrumb">
                Yönetim <span aria-hidden="true">/</span>{' '}
                {GROUPS.find((g) => g.keys.includes(active))?.title}{' '}
                <span aria-hidden="true">/</span>{' '}
                <strong>{SECTIONS.find(([key]) => key === active)?.[1]}</strong>
              </p>
            </div>
            <Suspense fallback={<PageState loading />}>
              <section
                key={`${sessionGeneration}-${active}-${['audit', 'events', 'feedback', 'reports', 'salons', 'roles', 'staff', 'store', 'bans', 'users', 'live', 'photos'].includes(active) ? '' : routePath}`}
                className="page-body"
              >
                {!sectionAllowed ? (
                  <>
                    <h1>Bu bölüme erişiminiz yok</h1>
                    <button
                      onClick={() => navigate(admin.role === 'FINANCE' ? 'economy' : 'stats')}
                    >
                      Yetkili bölüme git
                    </button>
                  </>
                ) : (
                  <>
                    {active === 'live' && <LivePage role={enumText(admin.role)} />}
                    {active === 'system' && <SystemPage />}
                    {active === 'games' && <GamesPage />}
                    {active === 'events' && <EventsPage />}
                    {active === 'economy' && (
                      <EconomyPage role={enumText(admin.role)} email={admin.email} />
                    )}
                    {active === 'web-odemeler' && (
                      <WebOdemelerPage
                        onOyuncuAc={
                          visibleSections.some(([key]) => key === 'users') ? openUser : undefined
                        }
                      />
                    )}
                    {active === 'moderasyon' && <ModerasyonKuyrugu onOpenUser={openUser} />}
                    {active === 'reports' && (
                      <ReportsPage role={enumText(admin.role)} onOpenUser={openUser} />
                    )}
                    {active === 'stats' && (
                      <StatsPage onNavigate={(key) => navigate(key as Section)} />
                    )}
                    {active === 'users' && <UsersPage role={admin.role} email={admin.email} />}
                    {active === 'roles' && (
                      <RolesPage onOpenUser={openUser} canReadHolders={admin.role !== 'SUPPORT'} />
                    )}
                    {active === 'permissions' && (
                      <PermissionsPage role={admin.role} capabilities={admin.capabilities} />
                    )}
                    {active === 'staff' && <StaffPage role={admin.role} email={admin.email} />}
                    {active === 'bans' && <BansPage onOpenUser={openUser} />}
                    {active === 'maintenance' && <MaintenancePage />}
                    {active === 'photos' && <PhotosPage onOpenUser={openUser} />}
                    {active === 'feedback' && <FeedbackPage onOpenUser={openUser} />}
                    {active === 'perf' && <PerfPage />}
                    {active === 'raporlar' && <RaporlarPage />}
                    {active === 'salons' && <SalonsPage />}
                    {active === 'store' && <StorePage />}
                    {active === 'policies' && <PoliciesPage role={enumText(admin.role)} />}
                    {active === 'daily' && <DailyBonusPage />}
                    {active === 'ekonomi-varsayilan' && <EkonomiVarsayilanPage />}
                    {active === 'wheel' && <WheelPage />}
                    {active === 'chat' && <ChatPage />}
                    {active === 'radio' && <RadioPage superAdmin={admin.role === 'SUPER_ADMIN'} />}
                    {active === 'bots' && <BotsPage onOpenUser={openUser} />}
                    {active === 'slot' && <SlotPage />}
                    {active === 'flags' && <FlagsPage />}
                    {active === 'announcements' && <AnnouncementsPage />}
                    {active === 'audit' && <AuditPage />}
                  </>
                )}
              </section>
            </Suspense>
          </main>
        </div>
      </AuditNavigation.Provider>
    </CapabilitiesContext.Provider>
  );
}

function Login({ onLogin }: { onLogin: (a: AdminSession) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="center">
      <form
        className="card login"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            const r = await api.login(email, password, totp);
            setToken(r.token);
            const session = await api.me();
            onLogin({
              email: session.admin.email,
              role: session.admin.role,
              capabilities: session.capabilities ?? [],
            });
          } catch (ex) {
            setErr(ex instanceof ApiError ? `${ex.code}: ${ex.message}` : String(ex));
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="brand">
          <img className="brand-logo" src={brandWordmark} alt="Panpa" />
          <span>
            <small>
              Yönetim
              <br />
              merkezi
            </small>
          </span>
        </div>
        <h1>Hesabına giriş yap</h1>
        <label>
          E-posta
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label>
          Parola
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <label>
          TOTP kodu
          <input
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
            required
          />
        </label>
        {err && <div className="error">{err}</div>}
        <button className="primary" disabled={busy}>
          {busy ? 'Giriş yapılıyor…' : 'Giriş yap'}
        </button>
        <p className="muted small">
          Ayrı kimlik alanı · zorunlu 2FA · 8 saatlik oturum · her işlem denetim günlüğünde.
        </p>
      </form>
    </div>
  );
}
